import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useNavigate } from 'react-router-dom';
import './ProductRangePage.css';

gsap.registerPlugin(ScrollTrigger);

const liquidProducts = [
  {
    name: 'BIO-ORBI Super Novel',
    hindi: 'बायो-ऑर्बी सुपर नॉवेल',
    tagline: 'Banana Pseudostem Organic Input',
    img: '/products/bio-orbi-super-novel.png',
    badge: 'Flagship',
    badgeColor: 'orange',
    isFlagship: true,
    features: [
      'Upcycles banana pseudostem waste (Fibre, Scutcher, Sap, Central Core).',
      'Government-patent-filed via ICAR (Institute of Govt. of India).',
      'Developed in technical collaboration with Navsari Agricultural University.',
    ],
    benefits: [
      'Rich in natural Potassium (K), Phosphorus (P), Calcium (Ca), and Magnesium (Mg).',
      'Retains soil moisture longer, shielding roots during harsh dry weather.',
      'Provides an eco-friendly, zero-chemical, high-yield plant booster.',
    ],
    buyLink: 'https://gem.gov.in/',
  },
  {
    name: 'BIO-ORBI Super',
    hindi: 'बायो-ऑर्बी सुपर',
    tagline: 'Liquid Fermented Organic Manure',
    img: '/products/bio-orbi-super.png',
    badge: 'Liquid',
    badgeColor: 'green',
    features: [
      'Fermented via beneficial micro-organisms (yeast, fungi, bacteria).',
      '100% liquid-soluble organic matter for complete absorption.',
      'Safe for application at any stage of crop growth cycle.',
    ],
    benefits: [
      'Improves crop resilience to biotic and abiotic stresses.',
      'Improves organic soil content to nurture earthworms and mycorrhizae.',
    ],
  },
  {
    name: 'BIO-ORBI Super-Prime',
    hindi: 'बायो-ऑर्बी सुपर-प्राइम',
    tagline: 'Potassium Mobilizing Biofertilizer (KMB)',
    img: '/products/bio-orbi-super-prime.png',
    badge: 'Liquid',
    badgeColor: 'green',
    features: [
      'Contains high-efficiency Potassium Mobilizing Organisms (KMO).',
      'Solubilizes locked-up soil potash, making it available to crops.',
      'Releases essential organic acids that break down mineral compounds.',
    ],
    benefits: [
      'Enhances crop growth, size, and shelf-life in salt-affected soils.',
      'Improves water-use efficiency and biotic/abiotic stress tolerance.',
    ],
  },
  {
    name: 'BIO-ORBI Prime',
    hindi: 'बायो-ऑर्बी प्राइम',
    tagline: 'Azotobacter Biofertilizer',
    img: '/products/bio-orbi-prime.png',
    badge: 'Liquid',
    badgeColor: 'green',
    features: [
      'Aerobic free-living nitrogen-fixing bacterium.',
      'Suitable for all non-leguminous plants (cotton, cereals, rice).',
      'Generates vital plant growth hormones (auxins, gibberellins).',
      'Suppresses harmful soil-borne pathogens.',
    ],
    benefits: [
      'Enhances crop yield potentials in tomatoes, eggplants, cotton, etc.',
      'Improves long-term soil microbial structure.',
    ],
  },
  {
    name: 'BIO-ORBI Super-Plus',
    hindi: 'बायो-ऑर्बी सुपर-प्लस',
    tagline: 'Phosphate Solubilizing Bacteria (PSB)',
    img: '/products/bio-orbi-super-plus.png',
    badge: 'Liquid',
    badgeColor: 'green',
    features: [
      'Contains phosphate-solubilizing bacteria strains.',
      'Converts locked-up, insoluble soil phosphates into soluble phosphorus.',
      'Promotes massive root-system development and branching.',
    ],
    benefits: [
      'Highly beneficial for all crops including oilseeds and pulses.',
      'Enhances uniform crop flowering, fruit setting, and overall vigour.',
    ],
  },
  {
    name: 'BIO-ORBI Plus',
    hindi: 'बायो-ऑर्बी प्लस',
    tagline: 'Rhizobium Biofertilizer',
    img: '/products/bio-orbi-plus.png',
    badge: 'Liquid',
    badgeColor: 'green',
    features: [
      'Symbiotic root-nodule forming bacterium.',
      'Specifically targets leguminous crop roots.',
      'Establishes natural nitrogen-fixation nodules directly at root sites.',
    ],
    benefits: [
      'Converts atmospheric nitrogen into ammonia for protein synthesis.',
      'Enriches plants with essential vitamins and nitrogen compounds.',
    ],
  },
];

const solidProducts = [
  {
    name: 'BIO-ORBI Organic Manure',
    hindi: 'बायो-ऑर्बी ऑर्गेनिक मन्योर',
    tagline: 'Soil Restorer & Conditioner',
    img: '/products/Organic Manure.png',
    badge: 'Solid',
    badgeColor: 'blue',
    features: [
      'Rich in premium humic substances and organic carbon.',
      'Restores structural integrity and aeration of depleted soils.',
      'Increases the Cation Exchange Capacity (CEC) to hold nutrients.',
    ],
    benefits: [
      'Improves water-holding capacity and counters soil compaction.',
      'Creates a long-term foundation for high-efficiency organic farming.',
    ],
  },
  {
    name: 'BIO-ORBI Bio Enriched Organic Manure',
    hindi: 'बायो-ऑर्बी एनरिच्ड ऑर्गेनिक मन्योर',
    tagline: 'Microbial Fortified Soil Input',
    img: '/products/Enriched Organic Manure.png',
    badge: 'Solid',
    badgeColor: 'blue',
    features: [
      'Fortified with active, beneficial nitrogen-fixing and PSB cultures.',
      'Blends high-quality organic matter with live biofertilizers.',
      'Boosts natural enzyme activity in the crop rhizosphere.',
    ],
    benefits: [
      'Supplies both immediate and sustained organic nourishment.',
      'Stimulates soil microflora to rapidly break down soil minerals.',
    ],
  },
  {
    name: 'BIO-ORBI Vermicompost',
    hindi: 'बायो-ऑर्बी वर्मीकम्पोस्ट',
    tagline: 'Premium Organic Worm Castings',
    img: '/products/Vermicompost.png',
    badge: 'Solid',
    badgeColor: 'blue',
    features: [
      '100% organic compost produced via scientific vermiculture.',
      'Rich in earthworm-derived enzymes, auxins, and humic acids.',
      'Contains vital trace elements in highly bio-available forms.',
    ],
    benefits: [
      'Accelerates seed germination and root system proliferation.',
      'Improves crop quality, colour, taste, and shelf-life.',
    ],
  },
];

const certifications = [
  { logo: '/logo/NPOP Certified.png', title: 'NPOP Certified', desc: "National Programme for Organic Production — India's standards for organic production" },
  { logo: '/logo/ISO 9001-2015.png', title: 'ISO 9001:2015', desc: 'International quality management standards across manufacturing and testing phases' },
  { logo: '/logo/ICAR Approved.png', title: 'ICAR Approved', desc: 'Product formulations reviewed and patent filed under ICAR frameworks' },
  { logo: '/logo/Eco-Packaging.png', title: 'Make in India', desc: 'Officially registered under the national Make in India initiative' },
  { logo: '/logo/logo_632abd28aea16.png', title: 'NAU Collaborated', desc: 'Developed in technical collaboration with Navsari Agricultural University' },
];

function ProductCard({ product, index }) {
  const navigate = useNavigate();

  const goToDetails = () => {
    const slug = product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    navigate(`/product/${slug}`);
    window.scrollTo(0, 0);
  };

  return (
    <div className={`prp-card${product.isFlagship ? ' prp-card--flagship' : ''}`}>
      <div className="prp-card__index">{String(index + 1).padStart(2, '0')}</div>
      <span className={`prp-card__badge prp-card__badge--${product.badgeColor}`}>{product.badge}</span>

      <div className="prp-card__img-wrap" onClick={goToDetails} title={`View ${product.name}`}>
        <img src={product.img} alt={product.name} className="prp-card__img" />
        {product.isFlagship && (
          <div className="prp-card__flagship-label">★ Govt. Patent Filed · GeM Listed</div>
        )}
      </div>

      <div className="prp-card__body">
        <h4 className="prp-card__name" onClick={goToDetails}>{product.name}</h4>
        <p className="prp-card__hindi">{product.hindi}</p>
        <span className="prp-card__tagline">{product.tagline}</span>

        <div className="prp-card__details">
          <h5>Key Features</h5>
          <ul className="prp-list prp-list--features">
            {product.features.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
          <h5>Key Benefits</h5>
          <ul className="prp-list prp-list--benefits">
            {product.benefits.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        </div>

        <div className="prp-card__cta">
          {product.isFlagship ? (
            <a href={product.buyLink} target="_blank" rel="noopener noreferrer" className="prp-btn prp-btn--gem">
              Buy on GeM ➔
            </a>
          ) : (
            <button className="prp-btn prp-btn--soon" disabled>Launching Soon</button>
          )}
          <button className="prp-btn prp-btn--outline" onClick={goToDetails}>View Details</button>
        </div>
      </div>
    </div>
  );
}

export default function ProductRangePage() {
  const rootRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = rootRef.current;
    if (!el) return;

    gsap.fromTo(
      el.querySelectorAll('.prp-hero__fade'),
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.9, stagger: 0.14, ease: 'power3.out' }
    );

    gsap.fromTo(
      el.querySelectorAll('.prp-card'),
      { y: 50, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.75, stagger: 0.09, ease: 'power2.out',
        scrollTrigger: { trigger: '.prp-grid', start: 'top 82%' }
      }
    );

    gsap.fromTo(
      el.querySelectorAll('.prp-cert-card'),
      { y: 30, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: { trigger: '.prp-certs__grid', start: 'top 82%' }
      }
    );

    return () => ScrollTrigger.getAll().forEach(t => t.kill());
  }, []);

  return (
    <div className="prp-root" ref={rootRef}>

      {/* ─── HERO ─── */}
      <section className="prp-hero">
        <div className="prp-hero__bg" style={{ backgroundImage: "url('/banner/organic_fertilizer_hero.png')" }} />
        <div className="prp-hero__overlay" />
        <div className="prp-container prp-hero__inner">
          <span className="prp-eyebrow prp-hero__fade">Our Product Range</span>
          <h1 className="prp-hero__h1 prp-hero__fade">
            Complete <span>Bio-Orbi</span> Portfolio
          </h1>
          <p className="prp-hero__sub prp-hero__fade">
            Scientifically formulated bio-fertilizers and organic soil conditioners — built to
            rebuild soil biology, strengthen roots, and maximise crop yield without a single
            synthetic chemical.
          </p>

          <div className="prp-hero__stats prp-hero__fade">
            {[
              { num: '9', label: 'Formulations' },
              { num: '1', label: 'Govt. Patent Filed' },
              { num: '100%', label: 'Organic' },
              { num: '20+', label: 'Yrs Experience' },
            ].map((s, i) => (
              <div key={i} className="prp-stat">
                <div className="prp-stat__num">{s.num}</div>
                <div className="prp-stat__label">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="prp-hero__btns prp-hero__fade">
            <a href="https://gem.gov.in/" target="_blank" rel="noopener noreferrer" className="prp-btn prp-btn--primary">
              Buy on GeM Portal ➔
            </a>
            <button className="prp-btn prp-btn--ghost" onClick={() => { navigate('/shop'); window.scrollTo(0, 0); }}>
              Browse Shop
            </button>
          </div>
        </div>
      </section>

      {/* ─── LIQUID SERIES ─── */}
      <section className="prp-section">
        <div className="prp-container">
          <div className="prp-section__hdr">
            <span className="prp-eyebrow">Liquid Bio-Fertilizers</span>
            <h2 className="prp-section__h2">Bio-Orbi <span>Liquid Series</span></h2>
            <p className="prp-section__desc">
              Our liquid bio-fertilizers harness beneficial bacteria and organic extracts to
              unlock soil nutrients, fix atmospheric nitrogen, and promote vigorous plant growth.
            </p>
          </div>
          <div className="prp-grid">
            {liquidProducts.map((p, i) => <ProductCard key={p.name} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* ─── MID BANNER ─── */}
      <section className="prp-mid-banner">
        <div className="prp-container">
          <div className="prp-mid-banner__inner">
            <div className="prp-mid-banner__text">
              <span className="prp-mid-banner__tag">100% Organic · Zero Chemicals · Zero Heavy Metals</span>
              <h3>Made in India. <span>Patent Filed</span> via ICAR.</h3>
              <p>
                Developed with Navsari Agricultural University and registered under the national
                Make in India initiative — every formulation is field-tested before it reaches
                a farmer's hands.
              </p>
            </div>
            <div className="prp-mid-banner__logos">
              {certifications.map((c, i) => (
                <img key={i} src={c.logo} alt={c.title} title={c.title} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── SOLID SERIES ─── */}
      <section className="prp-section prp-section--alt">
        <div className="prp-container">
          <div className="prp-section__hdr">
            <span className="prp-eyebrow">Solid Soil Conditioners</span>
            <h2 className="prp-section__h2">Bio-Orbi <span>Solid Series</span></h2>
            <p className="prp-section__desc">
              Restore depleted soils, improve moisture retention, and provide sustained
              nutrition through premium organic carbon and microbial-rich compost.
            </p>
          </div>
          <div className="prp-grid prp-grid--three">
            {solidProducts.map((p, i) => <ProductCard key={p.name} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* ─── CERTIFICATIONS ─── */}
      <section className="prp-certs">
        <div className="prp-container">
          <div className="prp-section__hdr prp-section__hdr--center">
            <span className="prp-eyebrow">Trust & Compliance</span>
            <h2 className="prp-section__h2">Backed by the <span>institutions that matter</span></h2>
          </div>
          <div className="prp-certs__grid">
            {certifications.map((c, i) => (
              <div key={i} className="prp-cert-card">
                <div className="prp-cert-card__logo"><img src={c.logo} alt={c.title} /></div>
                <h4>{c.title}</h4>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="prp-cta">
        <div className="prp-cta__bg" style={{ backgroundImage: "url('/banner/organic_fertilizer_hero.png')" }} />
        <div className="prp-cta__overlay" />
        <div className="prp-container prp-cta__inner">
          <span className="prp-eyebrow" style={{ color: '#f1921d' }}>Get in Touch</span>
          <h2>Ready to revive your soil?</h2>
          <p>
            Talk to our team about which Bio-Orbi formulation fits your crop, soil type, and
            season — backed by 20+ years of field experience and a government-filed patent.
          </p>
          <div className="prp-cta__btns">
            <a href="https://gem.gov.in/" target="_blank" rel="noopener noreferrer" className="prp-btn prp-btn--primary">
              Buy on GeM ➔
            </a>
            <button className="prp-btn prp-btn--ghost" onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }}>
              Contact Orbifert
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
